import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Card, Avatar, Typography, Box, Paper, Button, Grid,
    IconButton, CircularProgress, Skeleton, Tooltip, Chip, Stack
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import AssignmentIcon from "@mui/icons-material/Assignment";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import DateRangeIcon from "@mui/icons-material/DateRange";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import LogoutIcon from "@mui/icons-material/Logout";
import WorkOutlineRounded from "@mui/icons-material/WorkOutlineRounded";

import { CountDemandeEnAttente } from "../../../../services/DemandeService";
import { useSoldeYear } from "../../../../hooks/useSoldeYear";
import "./styles/CollaborateurPublicView.css";

const getInitials = (prenom = "", nom = "") =>
    `${prenom?.charAt(0)?.toUpperCase() || ""}${nom?.charAt(0)?.toUpperCase() || ""}`;

const ActionIcon = styled(IconButton)(({ theme }) => ({
    color: "#fff",
    border: `1px solid ${alpha("#fff", 0.35)}`,
    background: alpha("#000", 0.08),
    "&:hover": { background: alpha("#000", 0.16) }
}));

const StatTile = ({ icon, label, value, color = "primary" }) => (
    <Paper
        elevation={0}
        sx={(t) => ({
            px: 2, py: 1.75, borderRadius: 3,
            border: `1px solid ${alpha(t.palette[color].main, 0.25)}`,
            background: `linear-gradient(180deg, ${alpha(t.palette[color].main, 0.06)} 0%, ${alpha(
                t.palette.background.paper, 0.95
            )} 100%)`,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            transition: "transform .15s ease",
            "&:hover": { transform: "translateY(-2px)" }
        })}
    >
        <Box display="flex" alignItems="center" gap={1.25}>
            <Avatar sx={{ width: 34, height: 34, bgcolor: alpha("#000", 0.06), color: `${color}.main` }}>
                {icon}
            </Avatar>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Box>
        <Chip label={value} color={color} variant="outlined" sx={{ fontWeight: 800, borderRadius: 2 }} />
    </Paper>
);

const Ring = ({ value = 0, label = "" }) => (
    <Box position="relative" display="inline-flex">
        <CircularProgress variant="determinate" value={100} sx={{ color: "action.hover" }} size={80} thickness={4} />
        <CircularProgress
            variant="determinate"
            value={value}
            size={80}
            thickness={4}
            sx={{ position: "absolute", left: 0, color: "primary.main" }}
        />
        <Box
            sx={{ top: 0, left: 0, bottom: 0, right: 0, position: "absolute",
                display: "flex", alignItems: "center", justifyContent: "center" }}
        >
            <Box textAlign="center" lineHeight={1}>
                <Typography variant="subtitle2" fontWeight={800}>{`${value}%`}</Typography>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Box>
        </Box>
    </Box>
);

const ProfileCard = ({ collab, onLogout }) => {
    const [pendingCounts, setPendingCounts] = useState({ attestations: 0, conges: 0 });
    const [loadingCounters, setLoadingCounters] = useState(false);

    const [focusYear, setFocusYear] = useState(new Date().getFullYear());
    const collabId = collab?.id || collab?.uuid || null;

    const { data: solde, loading: loadingSolde } = useSoldeYear(collabId || undefined, focusYear);

    const used = useMemo(() => Math.max(0, solde?.consomme ?? 0), [solde]);
    const remaining = useMemo(() => Math.max(0, solde?.disponible ?? 0), [solde]);
    const total = used + remaining;
    const pctUsed = total > 0 ? Math.round((used / total) * 100) : 0;

    const loadCounters = useCallback(async () => {
        if (!collabId) return;
        setLoadingCounters(true);
        try {
            const { data } = await CountDemandeEnAttente(collabId);
            setPendingCounts({
                attestations: data?.attestations ?? 0,
                conges: data?.conges ?? 0
            });
        } catch {
            setPendingCounts({ attestations: 0, conges: 0 });
        } finally {
            setLoadingCounters(false);
        }
    }, [collabId]);

    useEffect(() => {
        void loadCounters();
    }, [loadCounters]);

    return (
        <Card className="glass-card" sx={{ p: 0, overflow: "hidden", borderRadius: 4, boxShadow: "0 18px 50px rgba(0,0,0,.08)" }}>
            <Box
                sx={(t) => ({
                    px: 3, pt: 3, pb: 2, color: "#fff",
                    background: `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.primary.dark})`,
                    position: "relative"
                })}
            >
                <Box
                    sx={{
                        position: "absolute", inset: 0, pointerEvents: "none",
                        background:
                            "radial-gradient(800px 200px at 12% -20%, rgba(255,255,255,.22), transparent)," +
                            "radial-gradient(600px 220px at 110% 0%, rgba(255,255,255,.14), transparent)"
                    }}
                />
                <Box display="flex" alignItems="center" gap={2} position="relative">
                    <Avatar
                        src={collab?.imageUrl || ""}
                        alt={`${collab?.prenom} ${collab?.nom}`}
                        sx={{ width: 64, height: 64, fontSize: 24, bgcolor: "#1976d2", boxShadow: "0 0 0 3px rgba(255,255,255,.35)" }}
                    >
                        {!collab?.imageUrl && getInitials(collab?.prenom, collab?.nom)}
                    </Avatar>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography fontWeight={900} sx={{ lineHeight: 1.1 }}>
                            {collab?.titre ? `${collab.titre}. ` : ""}{collab?.prenom} {collab?.nom}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                            <WorkOutlineRounded sx={{ fontSize: 16, opacity: .9 }} />
                            <Typography variant="caption" sx={{ opacity: .9 }}>
                                {collab?.fonction || "Collaborateur"}
                            </Typography>
                        </Box>
                    </Box>

                    <Tooltip title="Recharger les compteurs">
                        <ActionIcon
                            aria-label="refresh"
                            onClick={() => { void loadCounters(); }}
                            sx={{
                                ml: .5,
                                "@keyframes spin": { to: { transform: "rotate(360deg)" } },
                                animation: loadingCounters ? "spin 1s linear infinite" : "none"
                            }}
                        >
                            <RefreshRounded />
                        </ActionIcon>
                    </Tooltip>
                </Box>
            </Box>

            <Box sx={{ p: 3 }}>
                {loadingCounters ? (
                    <Grid container spacing={1.5}>
                        {[...Array(2)].map((_, i) => (
                            <Grid item xs={12} key={i}>
                                <Skeleton variant="rounded" height={64} />
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Grid container spacing={1.5}>
                        <Grid item xs={12}>
                            <StatTile
                                icon={<AssignmentIcon fontSize="small" />}
                                label="Attestations demandées"
                                value={pendingCounts.attestations}
                                color="primary"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <StatTile
                                icon={<DateRangeIcon fontSize="small" />}
                                label="Demandes de congé"
                                value={pendingCounts.conges}
                                color="secondary"
                            />
                        </Grid>
                    </Grid>
                )}

                <Box mt={2.5}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} gap={1} flexWrap="wrap">
                        <Typography variant="subtitle2" fontWeight={800}>
                            Solde congés — année {solde?.annee ?? focusYear}
                        </Typography>

                        <Stack direction="row" spacing={1} alignItems="center">
                            <IconButton size="small" title="Année précédente (-1)" onClick={() => setFocusYear((y) => y - 1)}>
                                <NavigateBeforeIcon />
                            </IconButton>
                            <IconButton size="small" title="Année suivante (+1)" onClick={() => setFocusYear((y) => y + 1)}>
                                <NavigateNextIcon />
                            </IconButton>
                        </Stack>

                    </Box>

                    {loadingSolde ? (
                        <Skeleton variant="rounded" height={88} />
                    ) : (
                        <Paper
                            elevation={0}
                            sx={(t) => ({
                                px: 2, py: 1.5, borderRadius: 3,
                                border: `1px solid ${alpha(t.palette.success.main, 0.25)}`,
                                background: `linear-gradient(180deg, ${alpha(t.palette.success.main, 0.04)} 0%, ${alpha(
                                    t.palette.background.paper, 0.98
                                )} 100%)`
                            })}
                        >
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box display="flex" alignItems="center" gap={1.25}>
                                    <Avatar sx={{ width: 34, height: 34, bgcolor: alpha("#00A76F", 0.1), color: "#00A76F" }}>
                                        <EventBusyIcon fontSize="small" />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Congés consommés / restants
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {used} utilisés • {remaining} restants
                                        </Typography>
                                    </Box>
                                </Box>

                                <Ring value={pctUsed} label="utilisés" />
                            </Box>
                        </Paper>
                    )}
                </Box>

                <Box display="flex" justifyContent="center" mt={3}>
                    <Button
                        onClick={onLogout}
                        variant="outlined"
                        size="small"
                        startIcon={<LogoutIcon />}
                        sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            px: 3,
                            color: "rgba(250,101,101,0.9)",
                            borderColor: "rgba(250,101,101,0.35)",
                            "&:hover": {
                                backgroundColor: "rgba(250,101,101,0.12)",
                                borderColor: "rgba(250,101,101,0.55)"
                            }
                        }}
                    >
                        Déconnexion
                    </Button>
                </Box>
            </Box>
        </Card>
    );
};

export default ProfileCard;
